// src/hooks/useTrainingAutoSubmit.ts
import { useCallback, useEffect, useRef } from "react";
import { submitTrainingFeedbackQueue } from "@/lib/training/feedbackSubmit";
import { useIsOnline } from "@/hooks/useIsOnline";

type RefLike<T> = { current: T };

export type UseTrainingAutoSubmitArgs = {
  enabled: boolean; // e.g. !!TRAINING_FEEDBACK_WEBHOOK_URL
  webhookUrl: string | null | undefined;

  version: string;

  salespersonRef: RefLike<{ name: string } | null>;
  locationRef: RefLike<{ id?: unknown; name?: unknown } | null>;

  // optional: allows you to call this from setup-complete, etc.
  onDebugLog?: (...args: any[]) => void;
};

export type UseTrainingAutoSubmitResult = {
  tryAutoSubmitTrainingFeedback: (reason: string) => Promise<void>;
};

export function useTrainingAutoSubmit({
  enabled,
  webhookUrl,
  version,
  salespersonRef,
  locationRef,
  onDebugLog,
}: UseTrainingAutoSubmitArgs): UseTrainingAutoSubmitResult {
  const isOnline = useIsOnline();
  const inFlightRef = useRef(false);
  const lastAttemptAtRef = useRef<number>(0);

  const tryAutoSubmitTrainingFeedback = useCallback(
    async (reason: string) => {
      if (!enabled) return;
      if (!webhookUrl) return;
      if (!navigator.onLine) return;

      const now = Date.now();
      if (now - lastAttemptAtRef.current < 10_000) return; // throttle
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      lastAttemptAtRef.current = now;

      const sp = salespersonRef.current;
      const loc = locationRef.current;

      try {
        onDebugLog?.("[training] submit attempt", { reason });

        await submitTrainingFeedbackQueue({
          webhookUrl,
          appVersion: version,
          locationId: typeof loc?.id === "string" ? loc.id : undefined,
          locationName: typeof loc?.name === "string" ? loc.name : undefined,
          salespersonFirstName: sp?.name?.split(" ")[0],
        });
      } catch (err) {
        onDebugLog?.("[training] submit failed", { reason, err });
      } finally {
        inFlightRef.current = false;
      }
    },
    [enabled, webhookUrl, version, salespersonRef, locationRef, onDebugLog]
  );

  // Mount + online-transition: try a submit each time we come online (or first mount while online).
  useEffect(() => {
    if (!enabled) return;
    if (!isOnline) return;
    void tryAutoSubmitTrainingFeedback("online-transition");
  }, [enabled, isOnline, tryAutoSubmitTrainingFeedback]);

  // While online, try every 60s (throttled + inFlight guarded)
  useEffect(() => {
    if (!enabled) return;

    const intervalId = window.setInterval(() => {
      if (!navigator.onLine) return;
      void tryAutoSubmitTrainingFeedback("online-interval");
    }, 60_000);

    return () => window.clearInterval(intervalId);
  }, [enabled, tryAutoSubmitTrainingFeedback]);

  return { tryAutoSubmitTrainingFeedback };
}
